import { useRef, useState } from "react";
import { Loader2, Plus, Upload, X } from "lucide-react";
import { api, apiError } from "../../lib/api";
import { imgSrc } from "../../data/artworks";
import { useCats } from "../../lib/settings";

const blank = { t: "", cat: "marin", img: "", tech: "Techniques mixtes sur toile", alt: "", desc: "", dim: "", annee: "", prix: "", vendu: false, photos: [], unique: true, stock: "" };
const inp = "w-full border border-[#15202B]/15 bg-white px-3 py-2.5 outline-none focus:border-[#15202B] text-[15px]";
const Lbl = ({ children, label }) => <label className="grid gap-1.5 min-w-0 text-xs tracking-[0.12em] uppercase text-[#55636F]">{label}{children}</label>;

export const ArtworkEditor = ({ art, onClose, onSaved }) => {
  const [f, setF] = useState(art ? { ...blank, ...art, prix: art.prix ?? "", stock: art.stock ?? "" } : blank);
  const [busy, setBusy] = useState(false); const [up, setUp] = useState(false); const [err, setErr] = useState("");
  const CATS = useCats();
  const file = useRef(null);
  const more = useRef(null);
  const [moreUp, setMoreUp] = useState("");
  const sendFile = async (fl) => { const fd = new FormData(); fd.append("file", fl); const { data } = await api.post("/admin/upload", fd); return data.url; };
  const uploadMore = async (e) => {
    const files = [...(e.target.files || [])].slice(0, 12 - (f.photos?.length || 0));
    setErr("");
    for (let i = 0; i < files.length; i++) {
      setMoreUp(`Envoi de la photo ${i + 1} sur ${files.length}…`);
      try { const url = await sendFile(files[i]); setF((p) => ({ ...p, photos: [...(p.photos || []), url] })); }
      catch (ex) { setErr(apiError(ex, "Une photo n'a pas pu être envoyée.")); break; }
    }
    setMoreUp(""); e.target.value = "";
  };
  const removePhoto = (i) => setF((p) => ({ ...p, photos: p.photos.filter((_, j) => j !== i) }));
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const upload = async (e) => {
    const fl = e.target.files?.[0]; if (!fl) return;
    setUp(true); setErr("");
    try { const url = await sendFile(fl); setF((p) => ({ ...p, img: url })); }
    catch (ex) { setErr(apiError(ex, "La photo n'a pas pu être envoyée.")); } finally { setUp(false); e.target.value = ""; }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!f.t.trim()) return setErr("Donnez un titre à l'œuvre.");
    if (!f.img) return setErr("Ajoutez la photo de l'œuvre.");
    setBusy(true); setErr("");
    const body = { t: f.t.trim(), cat: f.cat, img: f.img, tech: f.tech.trim(), alt: f.alt || f.t.trim(), desc: f.desc.trim(),
      dim: f.dim.trim(), annee: String(f.annee).trim(), prix: f.prix === "" ? null : Math.max(0, Math.round(+f.prix)), vendu: f.vendu, photos: f.photos || [], unique: f.unique !== false, stock: f.unique !== false || f.stock === "" ? null : Math.max(0, Math.round(+f.stock)) };
    try { const { data } = art?.id ? await api.put(`/admin/artworks/${art.id}`, body) : await api.post("/admin/artworks", body); onSaved(data, !art); }
    catch (ex) { setErr(apiError(ex)); setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#0C141B]/70 flex items-end sm:items-center justify-center sm:p-6" onClick={onClose}>
      <form onSubmit={save} onClick={(e) => e.stopPropagation()} data-testid="artwork-editor"
        className="relative w-full max-w-2xl max-h-[94vh] overflow-y-auto bg-[#F6F1E9] p-6 sm:p-8 grid grid-cols-1 gap-5 [&>*]:min-w-0">
        <button type="button" onClick={onClose} aria-label="Fermer" className="absolute top-4 right-4" data-testid="artwork-editor-close"><X className="w-5 h-5" /></button>
        <h2 className="f-display text-3xl">{art ? "Modifier l'œuvre" : "Ajouter une œuvre"}</h2>
        <div className="grid grid-cols-[110px_minmax(0,1fr)] gap-4 items-center">
          <div className="w-[110px] h-[110px] bg-[#E9E2D6] grid place-items-center overflow-hidden">
            {f.img ? <img src={imgSrc(f.img)} alt="Aperçu" className="w-full h-full object-cover" /> : <span className="text-xs text-[#55636F]">Aucune photo</span>}
          </div>
          <div className="grid gap-2">
            <input ref={file} type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif" className="hidden" onChange={upload} data-testid="artwork-editor-file" />
            <button type="button" onClick={() => file.current?.click()} disabled={up} className="btn-line justify-self-start !whitespace-normal" data-testid="artwork-editor-upload-button">
              {up ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}{f.img ? "Changer la photo" : "Choisir une photo"}
            </button>
            <span className="text-xs text-[#55636F]">JPG, PNG, WEBP ou HEIC (iPhone). La photo est réduite automatiquement.</span>
          </div>
        </div>
        <div className="grid gap-2">
          <span className="text-xs tracking-[0.12em] uppercase text-[#55636F]">Autres photos (détails, côtés, au mur…)</span>
          <div className="flex flex-wrap gap-3" data-testid="artwork-editor-photos">
            {(f.photos || []).map((p, i) => (
              <div key={p + i} className="relative w-16 h-16">
                <img src={imgSrc(p)} alt={`Photo ${i + 2}`} className="w-full h-full object-cover" />
                <button type="button" onClick={() => removePhoto(i)} aria-label="Retirer cette photo" data-testid={`artwork-editor-remove-photo-${i}`}
                  className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-[#15202B] text-white grid place-items-center"><X className="w-3 h-3" /></button>
              </div>
            ))}
            <input ref={more} type="file" multiple accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif" className="hidden" onChange={uploadMore} data-testid="artwork-editor-more-files" />
            <button type="button" onClick={() => more.current?.click()} disabled={!!moreUp || (f.photos?.length || 0) >= 12}
              className="w-16 h-16 border border-dashed border-[#8A6420] text-[#8A6420] grid place-items-center" aria-label="Ajouter des photos" data-testid="artwork-editor-add-photos">
              {moreUp ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-5 h-5" />}
            </button>
          </div>
          {moreUp && <span className="text-xs text-[#55636F]">{moreUp}</span>}
        </div>
        <Lbl label="Titre *"><input className={inp} value={f.t} onChange={set("t")} maxLength={120} data-testid="artwork-editor-title" /></Lbl>
        <div className="grid sm:grid-cols-2 gap-4">
          <Lbl label="Catégorie"><select className={inp} value={f.cat} onChange={set("cat")} data-testid="artwork-editor-category">
            {Object.entries(CATS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></Lbl>
          <Lbl label="Prix ($ CA)"><input className={inp} type="number" min="0" inputMode="numeric" placeholder="Vide : prix à venir" value={f.prix} onChange={set("prix")} data-testid="artwork-editor-price" /></Lbl>
        </div>
        <div className="grid sm:grid-cols-3 gap-4">
          <Lbl label="Technique"><input className={inp} value={f.tech} onChange={set("tech")} maxLength={120} data-testid="artwork-editor-tech" /></Lbl>
          <Lbl label="Dimensions"><input className={inp} placeholder="Ex. : 24 × 36 po" value={f.dim} onChange={set("dim")} maxLength={60} data-testid="artwork-editor-dim" /></Lbl>
          <Lbl label="Année"><input className={inp} placeholder="2026" value={f.annee} onChange={set("annee")} maxLength={10} data-testid="artwork-editor-year" /></Lbl>
        </div>
        <Lbl label="Description"><textarea className={`${inp} min-h-[120px]`} value={f.desc} onChange={set("desc")} maxLength={3000} data-testid="artwork-editor-desc" /></Lbl>
        <label className="flex items-center gap-3 text-[15px] cursor-pointer">
          <input type="checkbox" checked={f.unique !== false} onChange={set("unique")} className="w-5 h-5 accent-[#15202B]" data-testid="artwork-editor-unique" /> Pièce unique (une seule disponible). Décochez pour un produit vendu en plusieurs exemplaires.
        </label>
        {f.unique === false && (
          <Lbl label="Quantité en stock"><input className={inp} type="number" min="0" inputMode="numeric" placeholder="Vide : stock illimité" value={f.stock} onChange={set("stock")} data-testid="artwork-editor-stock" /></Lbl>
        )}
        <label className="flex items-center gap-3 text-[15px] cursor-pointer">
          <input type="checkbox" checked={f.vendu} onChange={set("vendu")} className="w-5 h-5 accent-[#15202B]" data-testid="artwork-editor-sold" /> Marquer comme vendue
        </label>
        {err && <p className="text-sm text-[#A2372F]" data-testid="artwork-editor-error">{err}</p>}
        <div className="flex flex-wrap gap-3 justify-end">
          <button type="button" onClick={onClose} className="btn-line">Annuler</button>
          <button type="submit" disabled={busy || up || !!moreUp} className="btn-ink" data-testid="artwork-editor-save">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enregistrer"}</button>
        </div>
      </form>
    </div>
  );
};
